import { useState, useEffect, useMemo } from 'react';
import apiService from '../../services/api.service';
import './Partners.css';
import ConnectorDetailsModal from '../../components/ConnectorDetailsModal';
import PartnerDetailsModal from '../../components/PartnerDetailsModal';

const Partners = () => {
    const [partners, setPartners] = useState([]);
    const [connectors, setConnectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');

    // Filtered partners based on search and filters
    const filteredPartners = useMemo(() => {
        return partners.filter(partner => {
            // Search filter
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch = !searchQuery ||
                partner.name?.toLowerCase().includes(searchLower) ||
                partner.gln?.toLowerCase().includes(searchLower) ||
                partner.email?.toLowerCase().includes(searchLower) ||
                partner.address?.toLowerCase().includes(searchLower);

            // Status filter
            const matchesStatus = statusFilter === 'all' ||
                (statusFilter === 'active' && partner.isActive) ||
                (statusFilter === 'inactive' && !partner.isActive);

            // Type filter
            const matchesType = typeFilter === 'all' ||
                (partner.partnerType || 'Trading Partner') === typeFilter;

            return matchesSearch && matchesStatus && matchesType;
        });
    }, [partners, searchQuery, statusFilter, typeFilter]);

    const fetchPartners = async () => {
        try {
            const response = await apiService.get('/partners');
            if (response.success) {
                setPartners(response.data);
            } else {
                setError('Failed to fetch partners');
            }
        } catch (err) {
            console.error('Error fetching partners:', err);
            setError('An error occurred while fetching partners');
        } finally {
            setLoading(false);
        }
    };

    const fetchConnectors = async () => {
        try {
            const data = await apiService.get('/as2/connectors');
            if (Array.isArray(data)) {
                setConnectors(data);
            }
        } catch (err) {
            console.error('Error fetching connectors:', err);
        }
    };

    useEffect(() => {
        fetchPartners();
        fetchConnectors();
    }, []);

    const [editingPartner, setEditingPartner] = useState(null);
    const [viewingPartner, setViewingPartner] = useState(null);
    const [connectorId, setConnectorId] = useState('');
    const [saving, setSaving] = useState(false);

    // Connector Details Modal State
    const [connectorDetailsModalOpen, setConnectorDetailsModalOpen] = useState(false);
    const [connectorForm, setConnectorForm] = useState({ name: '', email: '', address: '' });

    const getConnectorName = (id) => {
        if (!id) return 'Not Configured';
        const c = connectors.find(c => c.connectorId === id);
        return c ? (c.name || c.connectorId) : id;
    };

    const openConnectorDetails = () => {
        if (!connectorId) return;
        const c = connectors.find(c => c.connectorId === connectorId);
        setConnectorForm({
            name: c?.name || '',
            email: c?.email || '',
            address: c?.address || ''
        });
        setConnectorDetailsModalOpen(true);
    };

    const handleSaveConnectorDetails = async () => {
        try {
            const response = await apiService.post('/as2/connectors/details', {
                connectorId,
                ...connectorForm
            });
            if (response.success) {
                fetchConnectors();
                setConnectorDetailsModalOpen(false);
            } else {
                alert('Failed to save details');
            }
        } catch (err) {
            console.error('Error saving connector details:', err);
            alert('Error saving details');
        }
    };

    const handleEdit = (partner) => {
        setEditingPartner(partner);
        setConnectorId(partner.as2ConnectorId || '');
    };

    const handleDelete = async (partner) => {
        if (!confirm(`Are you sure you want to delete "${partner.name}"? This action cannot be undone.`)) {
            return;
        }
        try {
            const response = await apiService.delete(`/partners/${partner.id}`);
            if (response.success) {
                fetchPartners();
            } else {
                alert('Failed to delete partner');
            }
        } catch (err) {
            console.error('Error deleting partner:', err);
            alert('An error occurred while deleting');
        }
    };

    const handleSave = async () => {
        if (!editingPartner) return;

        setSaving(true);
        try {
            const partnerData = {
                name: editingPartner.name,
                gln: editingPartner.gln,
                sgln: editingPartner.sgln || null,
                address: editingPartner.address || '',
                email: editingPartner.email || null,
                phone: editingPartner.phone || null,
                partnerType: editingPartner.partnerType || 'Trading Partner',
                isActive: editingPartner.isActive !== false,
                as2ConnectorId: connectorId || null
            };

            let response;
            if (editingPartner.id) {
                response = await apiService.put(`/partners/${editingPartner.id}`, partnerData);
            } else {
                response = await apiService.post('/partners', partnerData);
            }

            if (response.success) {
                fetchPartners();
                setEditingPartner(null);
            } else {
                alert('Failed to save partner');
            }
        } catch (err) {
            console.error('Error saving partner:', err);
            alert('An error occurred while saving');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="partners-loading">Loading partners...</div>;
    if (error) return <div className="partners-error">{error}</div>;

    return (
        <>
            <header className="top-header">
                <div className="breadcrumb">
                    <span>Partner Connections</span>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">Address Book</span>
                </div>
            </header>
            <div className="address-book-page">
                {/* Page Header with Stats */}
                <div className="page-hero">
                    <div className="hero-content">
                        <h1>📚 Trading Partners Address Book</h1>
                        <p className="hero-subtitle">Manage your trading partner relationships and AS2 connections</p>
                    </div>
                    <button className="add-btn" onClick={() => {
                        setEditingPartner({
                            name: '',
                            gln: '',
                            sgln: '',
                            address: '',
                            email: '',
                            phone: '',
                            partnerType: 'Trading Partner',
                            isActive: true,
                            as2ConnectorId: '',
                            invitationStatus: 'not_sent'
                        });
                        setConnectorId('');
                    }}>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                        </svg>
                        Add Partner
                    </button>
                </div>

                {/* Stats Cards */}
                <div className="stats-row">
                    <div className="stat-card">
                        <div className="stat-icon total">👥</div>
                        <div className="stat-info">
                            <span className="stat-value">{partners.length}</span>
                            <span className="stat-label">Total Partners</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon active">✓</div>
                        <div className="stat-info">
                            <span className="stat-value">{partners.filter(p => p.isActive).length}</span>
                            <span className="stat-label">Active</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon pending">⏳</div>
                        <div className="stat-info">
                            <span className="stat-value">{partners.filter(p => p.invitationStatus === 'pending').length}</span>
                            <span className="stat-label">Pending Invites</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon connected">🔗</div>
                        <div className="stat-info">
                            <span className="stat-value">{partners.filter(p => p.as2ConnectorId).length}</span>
                            <span className="stat-label">AS2 Connected</span>
                        </div>
                    </div>
                </div>

                {/* Search and Filter Bar */}
                <div className="toolbar">
                    <div className="search-box">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>
                        <input
                            type="text"
                            placeholder="Search by name, GLN, email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="filter-group">
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="all">All Status</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                            <option value="all">All Types</option>
                            <option value="Trading Partner">Trading Partner</option>
                            <option value="Supplier">Supplier</option>
                            <option value="Customer">Customer</option>
                            <option value="CMO">CMO</option>
                        </select>
                    </div>
                </div>

                {/* Partners Table */}
                <div className="partners-table-container">
                    <table className="partners-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>GLN</th>
                                <th>Email</th>
                                <th>Type</th>
                                <th>Invitation</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPartners.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="empty-state">
                                        <div className="empty-content">
                                            <span className="empty-icon">📭</span>
                                            <p>No partners found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredPartners.map(partner => (
                                    <tr key={partner.id} className={!partner.isActive ? 'inactive-row' : ''}>
                                        <td>{partner.name}</td>
                                        <td><code>{partner.gln}</code></td>
                                        <td>{partner.email || '—'}</td>
                                        <td><span className="type-badge">{partner.partnerType || 'Trading Partner'}</span></td>
                                        <td>
                                            <span className={`invitation-badge ${partner.invitationStatus || 'not_sent'}`}>
                                                {partner.invitationStatus === 'accepted' ? 'Accepted' :
                                                    partner.invitationStatus === 'pending' ? 'Pending' : 'Not Sent'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-dot ${partner.isActive ? 'active' : 'inactive'}`}>
                                                {partner.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            <button className="text-btn view" onClick={() => setViewingPartner(partner)}>View</button>
                                            <button className="text-btn edit" onClick={() => handleEdit(partner)}>Edit</button>
                                            <button className="text-btn delete" onClick={() => handleDelete(partner)}>Delete</button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>


                {editingPartner && (
                    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setEditingPartner(null)}>
                        <div className="partner-modal">
                            <div className="modal-header">
                                <h2>{editingPartner.id ? 'Edit Partner' : 'Add New Partner'}</h2>
                                <button className="modal-close" onClick={() => setEditingPartner(null)}>
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </button>
                            </div>

                            <div className="modal-body">
                                {/* Basic Information Section */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <span className="section-icon">📋</span>
                                        <span>Basic Information</span>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-field">
                                            <label>Partner Name <span className="required">*</span></label>
                                            <input
                                                type="text"
                                                value={editingPartner.name || ''}
                                                onChange={(e) => setEditingPartner({ ...editingPartner, name: e.target.value })}
                                                placeholder="Acme Corporation"
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Partner Type</label>
                                            <select
                                                value={editingPartner.partnerType || 'Trading Partner'}
                                                onChange={(e) => setEditingPartner({ ...editingPartner, partnerType: e.target.value })}
                                            >
                                                <option value="Trading Partner">Trading Partner</option>
                                                <option value="Supplier">Supplier</option>
                                                <option value="Customer">Customer</option>
                                                <option value="CMO">CMO</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Identifiers Section */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <span className="section-icon">🔗</span>
                                        <span>Identifiers</span>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-field">
                                            <label>GLN <span className="required">*</span></label>
                                            <input
                                                type="text"
                                                value={editingPartner.gln || ''}
                                                onChange={(e) => setEditingPartner({ ...editingPartner, gln: e.target.value })}
                                                placeholder="0614141000012"
                                            />
                                            <span className="field-hint">13-digit Global Location Number</span>
                                        </div>
                                        <div className="form-field">
                                            <label>SGLN (Optional)</label>
                                            <input
                                                type="text"
                                                value={editingPartner.sgln || ''}
                                                onChange={(e) => setEditingPartner({ ...editingPartner, sgln: e.target.value })}
                                                placeholder="0614141000012.123"
                                            />
                                            <span className="field-hint">Sub-location extension</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Information Section */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <span className="section-icon">📧</span>
                                        <span>Contact Information</span>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-field">
                                            <label>Email</label>
                                            <input
                                                type="email"
                                                value={editingPartner.email || ''}
                                                onChange={(e) => setEditingPartner({ ...editingPartner, email: e.target.value })}
                                                placeholder="contact@partner.com"
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Phone</label>
                                            <input
                                                type="tel"
                                                value={editingPartner.phone || ''}
                                                onChange={(e) => setEditingPartner({ ...editingPartner, phone: e.target.value })}
                                                placeholder="+1 555-123-4567"
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row single">
                                        <div className="form-field">
                                            <label>Address</label>
                                            <input
                                                type="text"
                                                value={editingPartner.address || ''}
                                                onChange={(e) => setEditingPartner({ ...editingPartner, address: e.target.value })}
                                                placeholder="123 Main St, City, State 12345"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Connection Settings Section */}
                                <div className="form-section">
                                    <div className="section-header">
                                        <span className="section-icon">⚙️</span>
                                        <span>Connection Settings</span>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-field">
                                            <label>AS2 Connector</label>
                                            <div className="connector-select-group">
                                                <select
                                                    value={connectorId}
                                                    onChange={(e) => setConnectorId(e.target.value)}
                                                >
                                                    <option value="">-- Select Connector --</option>
                                                    {connectors.map(c => (
                                                        <option key={c.connectorId} value={c.connectorId}>
                                                            {c.name ? `${c.name}` : c.connectorId}
                                                        </option>
                                                    ))}
                                                </select>
                                                {connectorId && (
                                                    <button
                                                        type="button"
                                                        className="connector-edit-btn"
                                                        onClick={openConnectorDetails}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-field">
                                            <label>Status</label>
                                            <div className="status-toggle">
                                                <button
                                                    type="button"
                                                    className={`status-btn ${editingPartner.isActive !== false ? 'active' : ''}`}
                                                    onClick={() => setEditingPartner({ ...editingPartner, isActive: true })}
                                                >
                                                    Active
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`status-btn ${editingPartner.isActive === false ? 'inactive' : ''}`}
                                                    onClick={() => setEditingPartner({ ...editingPartner, isActive: false })}
                                                >
                                                    Inactive
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="btn-cancel"
                                    onClick={() => setEditingPartner(null)}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn-save"
                                    onClick={handleSave}
                                    disabled={saving || !editingPartner.name || !editingPartner.gln}
                                >
                                    {saving ? 'Saving...' : (editingPartner.id ? 'Save Changes' : 'Create Partner')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {connectorDetailsModalOpen && (
                    <ConnectorDetailsModal
                        isOpen={connectorDetailsModalOpen}
                        onClose={() => setConnectorDetailsModalOpen(false)}
                        connectorId={connectorId}
                        initialData={connectorForm}
                        onSave={fetchConnectors}
                    />
                )}

                {viewingPartner && (
                    <PartnerDetailsModal
                        isOpen={!!viewingPartner}
                        onClose={() => setViewingPartner(null)}
                        partner={viewingPartner}
                        connectorName={getConnectorName(viewingPartner.as2ConnectorId)}
                    />
                )}
            </div>
        </>
    );
};

export default Partners;

